import { Dispatch } from '@reduxjs/toolkit';
import { AppThunk } from '../store';
import {
	asyncCallStart,
	asyncCallError,
	asyncCallDone,
	fetchUserGroupSuccess,
	fetchUserGroupsSuccess,
	createUserGroupSuccess,
	updateUserGroupSuccess,
	deleteUserGroupSuccess,
} from '../stores/userGroupStore';
import * as UserGroupService from '../../services/user-group.service';
import {
	CreateUserGroupInput,
	UpdateUserGroupInput,
	AddMembersToUserGroupInput,
	RemoveMembersFromUserGroupInput,
	UserGroupFilter,
} from '../models/userGroup';
import { showAlert } from '../stores/alertStore';

export const fetchUserGroups =
	(filter: UserGroupFilter): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const response = await UserGroupService.getUserGroups(filter);
			dispatch(fetchUserGroupsSuccess(response));
		} catch (error) {
			console.error('Error fetching user groups:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to fetch groups. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const fetchUserGroup =
	(id: string): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const group = await UserGroupService.getUserGroup(id);
			dispatch(fetchUserGroupSuccess(group));
		} catch (error) {
			console.error('Error fetching user group:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to fetch group. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const createUserGroup =
	(input: CreateUserGroupInput): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const group = await UserGroupService.createUserGroup(input);
			dispatch(createUserGroupSuccess(group));
			dispatch(
				showAlert({
					message: 'Group created successfully!',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error creating user group:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to create group. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const updateUserGroup =
	(id: string, input: UpdateUserGroupInput): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const group = await UserGroupService.updateUserGroup(id, input);
			dispatch(updateUserGroupSuccess(group));
			dispatch(
				showAlert({
					message: 'Group updated successfully!',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error updating user group:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to update group. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const deleteUserGroup =
	(id: string): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			await UserGroupService.deleteUserGroup(id);
			dispatch(deleteUserGroupSuccess(id));
			dispatch(
				showAlert({
					message: 'Group deleted successfully!',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error deleting user group:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to delete group. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const addMembersToUserGroup =
	(id: string, input: AddMembersToUserGroupInput): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const group = await UserGroupService.addMembersToUserGroup(
				id,
				input
			);
			dispatch(updateUserGroupSuccess(group));
			dispatch(
				showAlert({
					message: 'Members added successfully!',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error adding members to user group:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to add members. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const removeMembersFromUserGroup =
	(id: string, input: RemoveMembersFromUserGroupInput): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const group = await UserGroupService.removeMembersFromUserGroup(
				id,
				input
			);
			dispatch(updateUserGroupSuccess(group));
			dispatch(
				showAlert({
					message: 'Members removed successfully!',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error removing members from user group:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to remove members. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};
